/**
 * Blog Content Data
 * Scalable content storage for programmatic blog posts
 */

import { BlogPost } from './seo';
import { visitFAQs } from './visit';

export interface BlogSection {
  type: 'paragraph' | 'heading' | 'list' | 'cta' | 'quote' | 'image' | 'sources';
  links?: { label: string; url: string }[];
  content?: string;
  items?: string[];
  level?: 2 | 3;
  ctaText?: string;
  ctaLink?: string;
  imageUrl?: string;
  imageAlt?: string;
}

export interface FullBlogPost extends BlogPost {
  sections: BlogSection[];
}

// ============================================================================
// FULL BLOG POST CONTENT
// ============================================================================

export const blogContent: Record<string, FullBlogPost> = {
  'signs-dog-needs-dental-cleaning': {
    slug: 'signs-dog-needs-dental-cleaning',
    title: '7 Signs Your Dog Needs a Professional Dental Cleaning',
    excerpt: 'Bad breath is just the beginning. Learn the warning signs that indicate your dog needs professional dental care before serious problems develop.',
    metaDescription: 'Learn the 7 warning signs that your dog needs professional dental cleaning. Bad breath, tartar buildup, and more. Fort Lauderdale veterinary dentist explains.',
    date: 'December 2024',
    datePublished: '2024-12-01',
    dateModified: '2024-12-24',
    readTime: '5 min read',
    category: 'Dog Dental Health',
    tags: ['dog dental health', 'dental cleaning', 'warning signs', 'pet health'],
    image: '/homeimagebouviercat.png',
    imageAlt: 'Dog showing healthy teeth after professional dental cleaning',
    author: 'CHOMP Pet Dental',
    featured: true,
    sections: [
      {
        type: 'paragraph',
        content: 'Did you know that by age 3, over 80% of dogs show signs of dental disease? As a veterinary dental specialist in Fort Lauderdale, we see the consequences of untreated dental problems every day. Here are the warning signs every dog owner should know.',
      },
      { type: 'heading', level: 2, content: '1. Persistent Bad Breath' },
      {
        type: 'paragraph',
        content: 'While "dog breath" is often joked about, truly foul-smelling breath is not normal. It\'s usually caused by bacteria buildup from plaque and tartar, or from infected gums. If your dog\'s breath makes you turn away, it\'s time for a dental check.',
      },
      { type: 'heading', level: 2, content: '2. Yellow or Brown Buildup on Teeth' },
      {
        type: 'paragraph',
        content: 'That yellowish-brown coating on your dog\'s teeth is tartar (calculus). Unlike plaque, which can be brushed away, tartar is hardite that requires professional cleaning to remove. The longer it stays, the more bacteria grow beneath the gumline.',
      },
      { type: 'heading', level: 2, content: '3. Red, Swollen, or Bleeding Gums' },
      {
        type: 'paragraph',
        content: 'Healthy gums should be pink and firm. Red, puffy, or bleeding gums indicate gingivitis or periodontal disease. This is painful for your dog, even if they don\'t show obvious signs.',
      },
      { type: 'heading', level: 2, content: '4. Difficulty Eating or Chewing on One Side' },
      {
        type: 'paragraph',
        content: 'If your dog suddenly becomes a picky eater, drops food, or chews on only one side of their mouth, dental pain is a likely culprit. Dogs are masters at hiding pain, so changes in eating habits are significant.',
      },
      { type: 'heading', level: 2, content: '5. Pawing at the Mouth or Face' },
      {
        type: 'paragraph',
        content: 'Dogs can\'t tell us their mouth hurts, but they often try to relieve discomfort by pawing at their face. If you notice this behavior, especially combined with other signs, schedule an exam promptly.',
      },
      { type: 'heading', level: 2, content: '6. Loose, Broken, or Missing Teeth' },
      {
        type: 'paragraph',
        content: 'Adult dogs should not lose teeth. If you notice a missing tooth or feel a loose one, your dog likely has advanced periodontal disease that has destroyed the bone supporting the tooth.',
      },
      { type: 'heading', level: 2, content: '7. Excessive Drooling or Blood in Saliva' },
      {
        type: 'paragraph',
        content: 'While some breeds naturally drool more, sudden increases in drooling—especially with blood—indicate oral pain or infection that needs immediate attention.',
      },
      { type: 'heading', level: 2, content: 'What Happens If You Wait?' },
      {
        type: 'paragraph',
        content: 'Dental disease doesn\'t just affect the mouth. Bacteria from infected gums enter the bloodstream and can damage the heart, liver, and kidneys. Ask your veterinarian how dental findings relate to your dog’s overall health.',
      },
      { type: 'heading', level: 2, content: 'The Good News' },
      {
        type: 'paragraph',
        content: 'Professional dental cleaning can reverse early dental disease and prevent progression. CHOMP Pet Dental in Fort Lauderdale focuses on dental care for dogs and cats. The veterinarian can explain the treatment recommended for your pet.',
      },
      {
        type: 'cta',
        content: 'Worried About Your Dog\'s Teeth?',
        ctaText: 'Book Free Consultation',
        ctaLink: '/book',
      },
    ],
  },
  'pet-dental-cleaning-cost-fort-lauderdale': {
    slug: 'pet-dental-cleaning-cost-fort-lauderdale',
    title: 'Dog & Cat Dental Cleaning Costs in Fort Lauderdale',
    excerpt: 'CHOMP’s published cleaning prices, separate fees, and what to ask before agreeing to a dental estimate.',
    metaDescription: 'CHOMP cat cleaning is $495 and dog cleaning is $595. Bloodwork is $150 extra. Compare core subtotals, extraction fees, and exam conditions.',
    date: 'September 2026',
    datePublished: '2024-12-01',
    dateModified: '2026-09-09',
    readTime: '4 min read',
    category: 'Cost & Value',
    tags: ['pet dental cost', 'Fort Lauderdale', 'dog teeth cleaning', 'cat teeth cleaning'],
    image: '/Chomp-Front.png',
    imageAlt: 'CHOMP Pet Dental clinic in Fort Lauderdale',
    author: 'CHOMP Pet Dental',
    featured: true,
    sections: [
      { type: 'paragraph', content: 'A cleaning price and a complete dental visit estimate can cover different things. CHOMP publishes its fees separately so you can see what is included, what may be added, and what to discuss with the veterinary team. These are CHOMP’s published prices, not a survey of Fort Lauderdale clinics.' },
      ...visitFAQs.slice(0, 3).flatMap(faq => [
        { type: 'heading' as const, level: 2 as const, content: faq.question },
        { type: 'paragraph' as const, content: faq.answer },
      ]),
      { type: 'heading', level: 2, content: 'What could change the total?' },
      { type: 'paragraph', content: 'The $645 cat and $745 dog core subtotals cover cleaning and bloodwork, with the exam fee waived when you proceed with recommended care. They exclude extractions, Periovive, medications, and other additional treatment. They are not a promise of the final bill. Your pet’s examination and dental findings determine the treatment plan.' },
      { type: 'heading', level: 2, content: 'How to compare two dental estimates' },
      { type: 'list', items: [
        'Ask whether the quote covers anesthesia and monitoring, dental X-rays, cleaning, and polishing.',
        'Check whether examination, bloodwork, extractions, medications, and follow-up are included or billed separately.',
        'Ask what can be determined before anesthesia and what may only be found during a complete examination and dental X-rays.',
        'Agree on how the practice will contact you and obtain authorization if additional treatment is needed.',
        'Compare the same scope of care. An awake surface cleaning is not equivalent to a veterinary dental procedure under anesthesia.',
      ] },
      { type: 'heading', level: 2, content: 'Why anesthesia and dental X-rays are part of the discussion' },
      { type: 'paragraph', content: 'AAHA’s dental guidelines describe examination, dental radiographs, and treatment under general anesthesia as parts of comprehensive dental care. The American Veterinary Dental College explains the limitations of anesthesia-free scaling. Anesthesia has risks; discuss your pet’s health, assessment, monitoring, and recovery plan with the veterinarian.' },
      { type: 'heading', level: 2, content: 'Payment options and your written estimate' },
      { type: 'paragraph', content: 'CHOMP lists Cherry and CareCredit among its financing options. Check eligibility and terms with the provider before relying on financing. If you have pet insurance, ask your insurer which dental services your policy covers and how reimbursement works.' },
      { type: 'heading', level: 2, content: 'Visit CHOMP in Fort Lauderdale' },
      { type: 'paragraph', content: 'Care takes place at our clinic at 1800 E Commercial Blvd, Fort Lauderdale, FL 33308. Call (954) 466-3775 or request an appointment online. The team will contact you to confirm appointment details.' },
      { type: 'sources', links: [
        { label: 'CHOMP services and published itemized fees', url: '/services' },
        { label: 'CHOMP veterinary team', url: '/team' },
        { label: 'AAHA Dental Care Guidelines for Dogs and Cats', url: 'https://www.aaha.org/public_documents/guidelines/aaha_dental_guidelines.pdf' },
        { label: 'AVDC position statement on anesthesia-free dentistry', url: 'https://avdc.org/download/30/position-statements/2876/dental-scaling-without-anesthesia.pdf' },
      ] },
      { type: 'cta', content: 'Discuss an estimate for your pet', ctaText: 'Request an appointment', ctaLink: '/book' },
    ],
  },
  'is-anesthesia-safe-for-pet-dental-cleaning': {
    slug: 'is-anesthesia-safe-for-pet-dental-cleaning',
    title: "Anesthesia for Pet Dental Cleaning: Risks and Questions to Ask",
    excerpt: "Why dental care uses anesthesia, what affects individual risk, and what to ask before your dog or cat’s procedure.",
    metaDescription: "Understand anesthesia for dog and cat dental cleaning: assessment, monitoring, preparation, recovery, and questions for your veterinarian.",
    date: 'December 2024',
    datePublished: '2024-12-01',
    dateModified: '2026-09-09',
    readTime: '3 min read',
    category: 'Safety',
    tags: ['anesthesia', 'pet safety', 'dental procedure', 'veterinary care'],
    image: '/Chomp-Front.png',
    imageAlt: 'CHOMP Pet Dental clinic in Fort Lauderdale',
    author: 'CHOMP Pet Dental',
    featured: false,
    sections: [
  {
    "type": "paragraph",
    "content": "General anesthesia allows a veterinarian to examine and treat the mouth while the patient is unconscious. It carries risks, including serious complications. Whether to proceed depends on your pet’s health, the dental problem, and the proposed care plan; no website can determine your pet’s individual risk."
  },
  {
    "type": "heading",
    "level": 2,
    "content": "Why dental procedures use general anesthesia"
  },
  {
    "type": "paragraph",
    "content": "AAHA’s dental guidelines recommend general anesthesia with airway protection for comprehensive dental care. It allows examination, dental X-rays, and cleaning beneath the gums. An awake surface cleaning does not provide the same examination or treatment. Anesthesia and pain control are related parts of the plan; ask how both will be managed."
  },
  {
    "type": "heading",
    "level": 2,
    "content": "Assessment and monitoring"
  },
  {
    "type": "paragraph",
    "content": "AAHA describes anesthesia as a process that begins before the procedure and continues through recovery. Health history, examination, and any indicated tests inform an individual plan. Bloodwork provides useful information, but normal results do not eliminate risk. Tell the team about medications, supplements, previous anesthetic problems, and changes in your pet’s health."
  },
  {
    "type": "list",
    "items": [
      "What health concerns affect my pet’s risk, and is further testing needed?",
      "Who monitors my pet, and how are breathing, circulation, and temperature assessed?",
      "What pain relief and recovery monitoring are planned?",
      "Could this procedure require referral or additional support?"
    ]
  },
  {
    "type": "heading",
    "level": 2,
    "content": "Older pets and existing health conditions"
  },
  {
    "type": "paragraph",
    "content": "Age is one part of the assessment. Heart, kidney, respiratory, and other conditions can affect the plan. AAHA highlights the need for additional planning in higher-risk patients and close attention to airway issues in short-nosed breeds. Ask the veterinarian to explain the expected benefit of treatment and the risks of proceeding or delaying it."
  },
  {
    "type": "heading",
    "level": 2,
    "content": "Before the visit and after discharge"
  },
  {
    "type": "paragraph",
    "content": "Request written instructions for food, water, and medications. Follow the treating team’s directions for your pet; do not use a generic fasting schedule or stop prescribed medication based on an article. Before going home, confirm feeding, activity, medication, follow-up, and whom to contact with recovery concerns."
  },
  {
    "type": "heading",
    "level": 2,
    "content": "Discussing care at Chomp"
  },
  {
    "type": "paragraph",
    "content": "Bring your questions and available veterinary records to the consultation. Ask which assessment and monitoring steps will apply to your pet and what the estimate includes."
  },
  {
    "type": "sources",
    "links": [
      {
        "label": "AAHA: General anesthesia for dental care",
        "url": "https://www.aaha.org/resources/2019-aaha-dental-care-guidelines-for-dogs-and-cats/general-anesthesia/"
      },
      {
        "label": "AAHA: Anesthesia and monitoring guidelines",
        "url": "https://www.aaha.org/globalassets/02-guidelines/2020-anesthesia/anesthesia_and_monitoring_guidelines_final.pdf"
      }
    ]
  },
  {
    "type": "cta",
    "content": "Discuss your pet’s dental care",
    "ctaText": "Request an Appointment",
    "ctaLink": "/book"
  }
]
  },
  'cat-dental-disease-signs-treatment': {
    slug: 'cat-dental-disease-signs-treatment',
    title: "Cat Dental Disease: Signs, Examination, and Treatment",
    excerpt: "Recognize possible dental problems in cats, understand the examination, and prepare questions about treatment and cost.",
    metaDescription: "Cat dental disease signs, dental X-rays, tooth resorption, treatment questions, and Chomp’s published cat cleaning fees in Fort Lauderdale.",
    date: 'December 2024',
    datePublished: '2024-12-01',
    dateModified: '2026-09-09',
    readTime: '3 min read',
    category: 'Cat Dental Health',
    tags: ['cat dental health', 'dental disease', 'feline dentistry', 'cat care'],
    image: '/Chomp-Front.png',
    imageAlt: 'CHOMP Pet Dental clinic in Fort Lauderdale',
    author: 'CHOMP Pet Dental',
    featured: false,
    sections: [
  {
    "type": "paragraph",
    "content": "Bad breath, drooling, difficulty eating, and changes in grooming can signal a dental problem in a cat. These signs can have other causes too. A veterinary examination is the starting point; how a mouth looks at home does not establish a diagnosis."
  },
  {
    "type": "heading",
    "level": 2,
    "content": "Common conditions"
  },
  {
    "type": "paragraph",
    "content": "Gingivitis affects the gums. Periodontitis damages the tissues supporting the teeth and can lead to tooth loss. Tooth resorption breaks down tooth structure and may be painful; dental X-rays help the veterinarian assess it and choose treatment. Some affected teeth require extraction."
  },
  {
    "type": "paragraph",
    "content": "Gingivostomatitis involves severe inflammation of the gums and other tissues in the mouth. It can interfere with eating and needs a treatment plan beyond a routine cleaning. Management may include extractions and medication, with follow-up to assess the response. Do not try to brush a painful, inflamed mouth."
  },
  {
    "type": "heading",
    "level": 2,
    "content": "What an examination can tell you"
  },
  {
    "type": "paragraph",
    "content": "An awake examination is an initial assessment. A complete dental evaluation may require anesthesia, probing, and dental X-rays. Ask what is known before the procedure and what may only become clear during it. The veterinarian can then explain which teeth need treatment and whether referral is appropriate."
  },
  {
    "type": "heading",
    "level": 2,
    "content": "Questions about treatment"
  },
  {
    "type": "list",
    "items": [
      "Which condition is suspected, and what tests will help confirm it?",
      "Will cleaning be sufficient, or are extractions or other treatments likely?",
      "How will the team discuss additional findings and changes to the estimate?",
      "What pain relief, feeding instructions, and follow-up will my cat need?"
    ]
  },
  {
    "type": "heading",
    "level": 2,
    "content": "Cat dental cleaning prices at Chomp"
  },
  {
    "type": "paragraph",
    "content": "Chomp publishes a $495 cat cleaning fee, with bloodwork billed separately at $150. The doctor exam is complimentary when you proceed with Chomp-recommended care and $50 if you do not proceed. Extractions, other indicated treatment, and medications can add to the total. Request an itemized estimate for your cat."
  },
  {
    "type": "cta",
    "content": "See included services and separate fees",
    "ctaText": "Read the Cost Guide",
    "ctaLink": "/blog/pet-dental-cleaning-cost-fort-lauderdale"
  },
  {
    "type": "heading",
    "level": 2,
    "content": "Home care after assessment"
  },
  {
    "type": "paragraph",
    "content": "Ask your veterinarian when brushing is appropriate and which products suit your cat. Brushing can help control plaque but does not treat established resorption or replace veterinary treatment. The timing of future examinations and cleanings depends on your cat’s findings."
  },
  {
    "type": "sources",
    "links": [
      {
        "label": "Cornell Feline Health Center: Feline dental disease",
        "url": "https://www.vet.cornell.edu/departments-centers-and-institutes/cornell-feline-health-center/health-information/feline-health-topics/feline-dental-disease"
      },
      {
        "label": "Cornell Feline Health Center: Gingivostomatitis",
        "url": "https://www.vet.cornell.edu/departments-centers-and-institutes/cornell-feline-health-center/health-information/feline-health-topics/gingivostomatitis"
      },
      {
        "label": "AAHA: Conscious oral evaluation",
        "url": "https://www.aaha.org/resources/2019-aaha-dental-care-guidelines-for-dogs-and-cats/conscious/"
      }
    ]
  },
  {
    "type": "cta",
    "content": "Arrange a dental assessment for your cat",
    "ctaText": "Request an Appointment",
    "ctaLink": "/book"
  }
]
  },
  'non-anesthetic-dental-cleaning-truth': {
    slug: 'non-anesthetic-dental-cleaning-truth',
    title: 'The Truth About Non-Anesthetic Dental Cleaning for Pets',
    excerpt: 'Non-anesthetic dental cleanings are popular but controversial. Here\'s what veterinary dentists say about their effectiveness.',
    metaDescription: 'The truth about non-anesthetic dental cleaning for pets. Learn why veterinary dentists don\'t recommend it and what works better.',
    date: 'December 2024',
    datePublished: '2024-12-01',
    dateModified: '2024-12-24',
    readTime: '5 min read',
    category: 'Education',
    tags: ['non-anesthetic dental', 'pet dental myths', 'dental cleaning', 'education'],
    image: '/homeimagebouviercat.png',
    imageAlt: 'Professional veterinary dental equipment',
    author: 'CHOMP Pet Dental',
    featured: false,
    sections: [
      {
        type: 'paragraph',
        content: 'Non-anesthetic dental cleanings (NAD) are offered at many pet stores, grooming salons, and even some veterinary clinics. They\'re appealing because they seem safer and cheaper. But do they actually work? Let\'s examine the facts.',
      },
      { type: 'heading', level: 2, content: 'What is Non-Anesthetic Dental Cleaning?' },
      {
        type: 'paragraph',
        content: 'Non-anesthetic dental cleaning involves manually scraping tartar from a pet\'s teeth while they\'re awake. The pet is typically restrained, and only the visible surfaces of the teeth are cleaned.',
      },
      { type: 'heading', level: 2, content: 'Why Veterinary Dentists Don\'t Recommend It' },
      {
        type: 'paragraph',
        content: 'The American Veterinary Dental College, American Animal Hospital Association, and virtually all veterinary dental specialists advise against non-anesthetic dental cleaning. Here\'s why:',
      },
      { type: 'heading', level: 3, content: '1. It Doesn\'t Treat Disease' },
      {
        type: 'paragraph',
        content: 'Dental disease occurs below the gumline, where bacteria attack the supporting structures of the teeth. Non-anesthetic cleaning only addresses the visible crown of the tooth—the part that isn\'t diseased.',
      },
      { type: 'heading', level: 3, content: '2. It\'s Stressful for Pets' },
      {
        type: 'paragraph',
        content: 'Being restrained while someone scrapes their teeth is frightening for most pets. The stress can cause increased heart rate, anxiety, and lasting fear of having their mouth touched.',
      },
      { type: 'heading', level: 3, content: '3. No Diagnostic Imaging' },
      {
        type: 'paragraph',
        content: 'Without anesthesia, dental X-rays are impossible. This means problems like tooth resorption, root abscesses, and bone loss go undetected.',
      },
      { type: 'heading', level: 3, content: '4. Risk of Aspiration' },
      {
        type: 'paragraph',
        content: 'Awake pets can inhale debris, bacteria, and water during the procedure. Under anesthesia, a tube protects the airway.',
      },
      { type: 'heading', level: 3, content: '5. Cosmetic, Not Medical' },
      {
        type: 'paragraph',
        content: 'Non-anesthetic cleaning makes teeth look cleaner but provides a false sense of security. Disease continues to progress unseen.',
      },
      { type: 'heading', level: 2, content: 'The Bottom Line' },
      {
        type: 'paragraph',
        content: 'Non-anesthetic dental cleaning may remove surface tartar, but it doesn\'t treat or prevent dental disease. It can actually delay proper treatment, allowing disease to worsen. If your pet needs dental care, professional cleaning under anesthesia is the only effective option.',
      },
      {
        type: 'quote',
        content: '"Non-professional dental scaling, with or without anesthesia, is considered unacceptable practice that can result in harm to the patient." — American Veterinary Dental College',
      },
      {
        type: 'cta',
        content: 'Get Real Dental Care for Your Pet',
        ctaText: 'Schedule Professional Cleaning',
        ctaLink: '/book',
      },
    ],
  },
  'how-often-dogs-need-dental-cleaning': {
    slug: 'how-often-dogs-need-dental-cleaning',
    title: 'How Often Do Dogs Need Dental Cleaning? Age-by-Age Guide',
    excerpt: 'Your dog\'s dental needs change with age. Learn the recommended cleaning schedule from puppy to senior.',
    metaDescription: 'How often should your dog get professional dental cleaning? Age-by-age guide from puppies to seniors. Fort Lauderdale vet advice.',
    date: 'December 2024',
    datePublished: '2024-12-15',
    dateModified: '2024-12-24',
    readTime: '5 min read',
    category: 'Dog Dental Health',
    tags: ['dog dental cleaning', 'cleaning frequency', 'dog age', 'preventive care'],
    image: '/homeimagebouviercat.png',
    imageAlt: 'Dogs of different ages showing healthy smiles',
    author: 'CHOMP Pet Dental',
    featured: false,
    sections: [
      {
        type: 'paragraph',
        content: 'One of the most common questions we receive is "How often does my dog need a dental cleaning?" The answer depends on several factors, including age, breed, and individual dental health. Here\'s a comprehensive guide.',
      },
      { type: 'heading', level: 2, content: 'Puppies (Under 1 Year)' },
      {
        type: 'paragraph',
        content: 'Puppies typically don\'t need professional cleanings. Focus on establishing good habits: daily brushing, dental toys, and getting them comfortable with having their mouth handled. Your vet should examine teeth during regular puppy visits.',
      },
      { type: 'heading', level: 2, content: 'Young Adults (1-3 Years)' },
      {
        type: 'paragraph',
        content: 'This is when dental disease often begins. Most dogs should have their first professional cleaning by age 3, or earlier if you notice tartar buildup. Small breeds and brachycephalic (flat-faced) breeds may need earlier intervention.',
      },
      { type: 'heading', level: 2, content: 'Adult Dogs (3-7 Years)' },
      {
        type: 'paragraph',
        content: 'Annual dental exams are essential. Most dogs in this age range benefit from professional cleaning every 1-2 years, depending on:',
      },
      {
        type: 'list',
        items: [
          'Breed predisposition (small breeds need more frequent cleaning)',
          'Home dental care routine',
          'Diet (wet food vs. dry)',
          'Individual tartar accumulation rate',
        ],
      },
      { type: 'heading', level: 2, content: 'Senior Dogs (7+ Years)' },
      {
        type: 'paragraph',
        content: 'Senior dogs often have accumulated dental disease and may need more frequent care. Annual or even bi-annual cleanings may be recommended. Don\'t let age deter you—senior dogs benefit greatly from dental care, and ask your veterinarian about an individual assessment before anesthesia.',
      },
      { type: 'heading', level: 2, content: 'Breed Considerations' },
      {
        type: 'paragraph',
        content: 'Some breeds are more prone to dental problems:',
      },
      {
        type: 'list',
        items: [
          'Small breeds (Yorkies, Chihuahuas, Poodles): Often need annual cleaning',
          'Brachycephalic breeds (Bulldogs, Pugs): Crowded teeth increase disease risk',
          'Greyhounds: Genetically prone to dental issues despite good care',
          'Large breeds: Generally less prone but still need regular exams',
        ],
      },
      { type: 'heading', level: 2, content: 'Signs Your Dog Needs Cleaning Now' },
      {
        type: 'paragraph',
        content: 'Don\'t wait for the schedule if you notice:',
      },
      {
        type: 'list',
        items: [
          'Bad breath that doesn\'t improve with brushing',
          'Visible yellow or brown tartar',
          'Red or bleeding gums',
          'Difficulty eating or dropping food',
          'Loose or broken teeth',
        ],
      },
      {
        type: 'cta',
        content: 'Not Sure If Your Dog Needs a Cleaning?',
        ctaText: 'Schedule Free Exam',
        ctaLink: '/book',
      },
    ],
  },
  'dog-bad-breath-causes-solutions': {
    slug: 'dog-bad-breath-causes-solutions',
    title: 'Why Does My Dog Have Bad Breath? Causes and Solutions',
    excerpt: 'Dog bad breath isn\'t normal. Learn what causes it and how professional dental care can eliminate it.',
    metaDescription: 'Why does your dog have bad breath? Learn the common causes and proven solutions from Fort Lauderdale veterinary dental specialists.',
    date: 'December 2024',
    datePublished: '2024-12-18',
    dateModified: '2024-12-24',
    readTime: '4 min read',
    category: 'Dog Dental Health',
    tags: ['dog bad breath', 'halitosis', 'dental disease', 'oral health'],
    image: '/homeimagebouviercat.png',
    imageAlt: 'Happy dog with fresh breath after dental treatment',
    author: 'CHOMP Pet Dental',
    featured: false,
    sections: [
      {
        type: 'paragraph',
        content: '"Dog breath" is so common it\'s become a joke. But truly foul-smelling breath is not normal—it\'s a sign of a problem. Here\'s what causes bad breath in dogs and what you can do about it.',
      },
      { type: 'heading', level: 2, content: 'The #1 Cause: Dental Disease' },
      {
        type: 'paragraph',
        content: 'In over 80% of cases, bad breath is caused by bacteria in the mouth. Plaque and tartar harbor billions of bacteria that produce sulfur compounds—the source of that rotten smell. The worse the dental disease, the worse the breath.',
      },
      { type: 'heading', level: 2, content: 'Other Causes of Bad Breath' },
      {
        type: 'list',
        items: [
          'Diet: Some foods cause temporary bad breath (fish-based diets, for example)',
          'Oral tumors: Growths in the mouth can become infected',
          'Kidney disease: Can cause breath with a urine-like or ammonia smell',
          'Diabetes: May cause sweet, fruity-smelling breath',
          'Eating non-food items: Dogs that eat garbage, feces, or dead animals will have temporary bad breath',
        ],
      },
      { type: 'heading', level: 2, content: 'Why Mints and Sprays Don\'t Work' },
      {
        type: 'paragraph',
        content: 'Products that mask bad breath don\'t address the cause. They\'re like spraying air freshener on garbage—the smell returns because the source is still there. The only way to permanently eliminate dental-related bad breath is professional cleaning.',
      },
      { type: 'heading', level: 2, content: 'The Solution: Professional Dental Cleaning' },
      {
        type: 'paragraph',
        content: 'A professional cleaning removes the tartar and bacteria causing bad breath. After cleaning, most dogs have noticeably fresher breath within 24-48 hours. Combined with home care, fresh breath can last for months.',
      },
      { type: 'heading', level: 2, content: 'Home Care to Maintain Fresh Breath' },
      {
        type: 'list',
        items: [
          'Daily brushing with pet-safe toothpaste',
          'Dental chews approved by the Veterinary Oral Health Council',
          'Water additives designed for dental health',
          'Regular professional cleanings as recommended',
        ],
      },
      {
        type: 'cta',
        content: 'Tired of Your Dog\'s Bad Breath?',
        ctaText: 'Schedule Dental Cleaning',
        ctaLink: '/book',
      },
    ],
  },
  'pet-dental-disease-heart-kidney-connection': {
    slug: 'pet-dental-disease-heart-kidney-connection',
    title: 'How Dental Disease Affects Your Pet\'s Heart, Kidneys, and Liver',
    excerpt: 'Dental disease doesn\'t stay in the mouth. Learn how bacteria spread and damage vital organs.',
    metaDescription: 'How pet dental disease affects heart, kidneys, and liver health. Learn why treating dental disease can extend your pet\'s life.',
    date: 'December 2024',
    datePublished: '2024-12-20',
    dateModified: '2024-12-24',
    readTime: '6 min read',
    category: 'Pet Health',
    tags: ['dental disease', 'organ health', 'heart disease', 'kidney disease'],
    image: '/homeimagebouviercat.png',
    imageAlt: 'Healthy pet receiving veterinary checkup',
    author: 'CHOMP Pet Dental',
    featured: true,
    sections: [
      {
        type: 'paragraph',
        content: 'When we talk about dental disease, many pet owners think it\'s "just" about bad breath or losing teeth. But dental disease is a systemic problem that affects your pet\'s entire body. Here\'s how bacteria in the mouth can damage vital organs.',
      },
      { type: 'heading', level: 2, content: 'How Bacteria Travel Through the Body' },
      {
        type: 'paragraph',
        content: 'In periodontal disease, bacteria infect the gums and destroy tissue. This creates pockets where bacteria multiply, and the inflamed, bleeding gums provide a direct pathway to the bloodstream. With every heartbeat, bacteria are pumped throughout the body.',
      },
      { type: 'heading', level: 2, content: 'Effects on the Heart' },
      {
        type: 'paragraph',
        content: 'Dental bacteria have been found in heart tissue of dogs with periodontal disease. They can:',
      },
      {
        type: 'list',
        items: [
          'Colonize heart valves, causing endocarditis (infection of the heart lining)',
          'Contribute to heart murmurs',
          'Accelerate existing heart conditions',
          'Increase risk of heart failure',
        ],
      },
      {
        type: 'paragraph',
        content: 'Studies show dogs with severe periodontal disease are 6 times more likely to develop endocarditis than dogs with healthy mouths.',
      },
      { type: 'heading', level: 2, content: 'Effects on the Kidneys' },
      {
        type: 'paragraph',
        content: 'The kidneys filter blood, so they\'re exposed to any bacteria circulating in the bloodstream. Dental bacteria can:',
      },
      {
        type: 'list',
        items: [
          'Cause kidney infections (pyelonephritis)',
          'Trigger chronic inflammation damaging kidney tissue',
          'Accelerate chronic kidney disease progression',
        ],
      },
      { type: 'heading', level: 2, content: 'Effects on the Liver' },
      {
        type: 'paragraph',
        content: 'The liver processes toxins and is vulnerable to bacterial assault:',
      },
      {
        type: 'list',
        items: [
          'Bacteria can cause liver abscesses',
          'Chronic inflammation leads to hepatitis',
          'Liver function may decline over time',
        ],
      },
      { type: 'heading', level: 2, content: 'The Lifespan Connection' },
      {
        type: 'paragraph',
        content: 'Research from Purdue University found that dogs with severe dental disease live an average of 1-3 years less than dogs with healthy mouths. A study of over 50,000 dogs showed clear connections between dental health and longevity.',
      },
      { type: 'heading', level: 2, content: 'Prevention is the Best Medicine' },
      {
        type: 'paragraph',
        content: 'The good news: treating dental disease stops the bacterial assault on organs. Many pet owners report significant improvements in energy, appetite, and overall health after their pet\'s dental cleaning.',
      },
      {
        type: 'cta',
        content: 'Protect Your Pet\'s Whole-Body Health',
        ctaText: 'Schedule Dental Cleaning',
        ctaLink: '/book',
      },
    ],
  },
};

/**
 * Get full blog post content by slug
 */
export const getFullBlogPost = (slug: string): FullBlogPost | undefined => {
  return blogContent[slug];
};

/**
 * Get all full blog posts
 */
export const getAllFullBlogPosts = (): FullBlogPost[] => {
  return Object.values(blogContent);
};



